import { weatherLabel } from './assembly-day-log.schema';

/**
 * Zusammenfassung eines Bau-Tagesberichts für die **Unterschrift**
 * (ADR 0074, ergänzt 2026-08-10).
 *
 * Der Anlass: Der Bauherr bekam bisher ein leeres Unterschriftsfeld gereicht
 * und zeichnete gegen, ohne gesehen zu haben, **was** er gegenzeichnet. Eine
 * blind geleistete Unterschrift entwertet genau das, wofür der Tagesbericht da
 * ist — sie ist im Streitfall angreifbar, und fachlich ist sie eine Zumutung.
 *
 * Hier steht deshalb die Logik, welche Angaben in der Vorschau erscheinen und
 * wie sie heißen. Bewusst geteilt zwischen der internen Maske und dem Portal:
 * Es ist dasselbe Dokument und derselbe Unterzeichner — zwei Zusammenfassungen
 * würden irgendwann Unterschiedliches behaupten.
 */

/** Eine Zeile der Vorschau. */
export interface DayLogSummaryRow {
  label: string;
  /** Angezeigter Wert; bei leerem Feld der `emptyText` der Zeile. */
  value: string;
  /**
   * Ist das Feld tatsächlich leer? Für die Darstellung — „keine" soll man von
   * einer echten Angabe unterscheiden können.
   */
  isEmpty: boolean;
  /**
   * **Aussage-Zeile.** Behinderungen, besondere Vorkommnisse und
   * Regieleistungen werden AUCH LEER gezeigt: „keine Behinderungen" ist
   * rechtlich eine Aussage, keine fehlende Angabe. Wer sie ausblendet, lässt
   * den Bauherrn etwas unterschreiben, das er nicht gelesen hat.
   */
  isStatement: boolean;
}

/** Der Ausschnitt eines Tagesberichts, den die Vorschau braucht. */
export interface SummarizableDayLog {
  logDate: string;
  number: number | null;
  weatherConditions: string[];
  temperatureC: number | null;
  workFrom: string | null;
  workTo: string | null;
  workHours: number | null;
  travelHours: number | null;
  workerCount: number | null;
  work: string | null;
  obstructions: string | null;
  specialEvents: string | null;
  extraWork: string | null;
  workers: { name: string; role: string; hours: number | null }[];
}

const numberFormat = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 });

function hours(value: number | null): string {
  return value === null ? '' : `${numberFormat.format(value)} h`;
}

/**
 * Personal: namentlich, wenn erfasst — sonst die grobe Anzahl. Dieselbe
 * Rangfolge wie im Bericht selbst (ADR 0074 §3): Namentliche Zeilen gewinnen
 * gegen die Anzahl, sonst stünden zwei Zahlen für denselben Aufwand nebeneinander.
 */
function crewLine(day: SummarizableDayLog): string {
  if (day.workers.length > 0) {
    return day.workers
      .map((worker) => (worker.hours === null ? worker.name : `${worker.name} (${hours(worker.hours)})`))
      .join(', ');
  }
  if (day.workerCount !== null) {
    return `${day.workerCount} ${day.workerCount === 1 ? 'Person' : 'Personen'}`;
  }
  return '';
}

function timeRange(day: SummarizableDayLog): string {
  if (day.workFrom && day.workTo) return `${day.workFrom} – ${day.workTo}`;
  if (day.workFrom) return `ab ${day.workFrom}`;
  if (day.workTo) return `bis ${day.workTo}`;
  return '';
}

function weather(day: SummarizableDayLog): string {
  const conditions = day.weatherConditions.map((value) => weatherLabel(value) ?? value).join(', ');
  const temperature = day.temperatureC === null ? '' : `${numberFormat.format(day.temperatureC)} °C`;
  return [conditions, temperature].filter(Boolean).join(' · ');
}

function row(label: string, raw: string, options: { emptyText?: string; isStatement?: boolean } = {}): DayLogSummaryRow {
  const value = raw.trim();
  return {
    label,
    value: value || (options.emptyText ?? '—'),
    isEmpty: value.length === 0,
    isStatement: options.isStatement ?? false,
  };
}

/**
 * Die Zeilen der Unterschrifts-Vorschau, in Lesereihenfolge.
 *
 * Leere **Angaben** (Witterung, Zeiten, Personal) fallen heraus — eine Liste
 * aus Strichen liest niemand. Leere **Aussagen** (Arbeiten, Behinderungen,
 * Vorkommnisse, Regieleistungen) bleiben stehen: Dass an diesem Tag *nichts*
 * behindert hat, ist der Inhalt, den der Bauherr bestätigt.
 */
export function summarizeDayLog(day: SummarizableDayLog): DayLogSummaryRow[] {
  const facts = [
    row('Witterung', weather(day)),
    row('Arbeitszeit', timeRange(day)),
    row('Arbeitsstunden', hours(day.workHours)),
    row('Reisezeit', hours(day.travelHours)),
    row('Personal', crewLine(day)),
  ].filter((entry) => !entry.isEmpty);

  const statements = [
    row('Ausgeführte Arbeiten', day.work ?? '', {
      emptyText: 'nichts eingetragen',
      isStatement: true,
    }),
    row('Behinderungen / Erschwernisse', day.obstructions ?? '', {
      emptyText: 'keine',
      isStatement: true,
    }),
    row('Besondere Vorkommnisse', day.specialEvents ?? '', {
      emptyText: 'keine',
      isStatement: true,
    }),
    row('Regieleistungen', day.extraWork ?? '', { emptyText: 'keine', isStatement: true }),
  ];

  return [...facts, ...statements];
}
